import React, { useState, useEffect } from 'react'
import { API_URL } from '../config/api'
import { Input, Button, message, Badge } from 'antd'
import { MessageOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice'
import GuestRealTimeChatWindow from '../components/CustomerService/GuestRealTimeChatWindow'
import ChatNotificationListener from '../components/CustomerService/ChatNotificationListener'
import { useLanguage } from '../contexts/LanguageContext'

const LoginPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t, translateText } = useLanguage()
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [showChat, setShowChat] = useState(false)
  const [chatMinimized, setChatMinimized] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [guestId] = useState(() => {
    // 生成或获取访客ID
    let id = localStorage.getItem('guestId')
    if (!id) {
      id = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('guestId', id)
    }
    return id
  })

  // 如果已经登录且当前在登录页，才跳转到主页面
  // 避免在其他页面刷新时触发不必要的跳转
  useEffect(() => {
    if (isAuthenticated && window.location.pathname === '/login') {
      navigate('/home')
    }
  }, [isAuthenticated, navigate])

  const handleLogin = async () => {
    if (!account || !password) {
      message.error(t('login.enterAccountPassword'))
      return
    }

    setLoading(true)
    dispatch(loginStart())

    try {
      // 判断是邮箱还是账号
      const isEmail = account.includes('@')
      const loginEmail = isEmail ? account : `${account}@account.local`

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginEmail,
          password: password,
          isAdminLogin: false, // 普通用户登录入口
        }),
      })

      const data = await response.json()

      if (data.success) {
        const user = {
          id: data.data.user.id,
          email: data.data.user.email,
          name: data.data.user.name,
          kycStatus: data.data.user.kycStatus,
          accountType: data.data.user.accountType,
        }

        dispatch(loginSuccess({ user, token: data.data.token }))
        message.success(t('login.loginSuccess'))

        // 普通用户登录成功，跳转到主页
        setTimeout(() => {
          navigate('/home')
        }, 1000)
      } else {
        // 将后端错误消息映射到国际化文本
        const errorMessage = data.error?.message || ''
        let translatedError = t('login.loginFailed')
        
        if (errorMessage.includes('Invalid') || errorMessage.includes('password') || errorMessage.includes('incorrect') || errorMessage.includes('密码')) {
          translatedError = t('login.invalidCredentials')
        } else if (errorMessage.includes('not found') || errorMessage.includes('User') || errorMessage.includes('不存在')) {
          translatedError = t('login.userNotFound')
        } else if (errorMessage) {
          translatedError = translateText(errorMessage)
        }
        
        dispatch(loginFailure(translatedError))
        message.error(translatedError)
      }
    } catch (error) {
      dispatch(loginFailure(t('login.networkError')))
      message.error(t('login.networkError'))
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e13] flex flex-col">
      {/* 后台消息监听 */}
      <ChatNotificationListener 
        guestId={guestId}
        isActive={showChat}
        onNewMessage={(count) => setUnreadCount(count)}
      />
      
      {/* 主内容区域 */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* 标题 */}
          <h1 className="text-white text-2xl font-bold text-center mb-12">
            {t('login.title')}
          </h1>

          {/* 登录表单 */}
          <div className="space-y-6">
            <div>
              <label className="text-white text-sm font-medium block mb-2">
                {t('login.account')}
              </label>
              <Input
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('login.accountPlaceholder')}
                className="h-12 bg-transparent border-gray-700 text-white placeholder-gray-500"
              />
            </div>

            <div>
              <label className="text-white text-sm font-medium block mb-2">
                {t('login.password')}
              </label>
              <Input.Password
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t('login.passwordPlaceholder')}
                className="h-12 bg-transparent border-gray-700 text-white placeholder-gray-500"
              />
            </div>

            <Button
              onClick={handleLogin}
              loading={loading}
              className="w-full h-12 bg-[#f0b90b] border-none text-black font-medium hover:bg-[#f0b90b]/90 mt-8"
            >
              {t('login.loginButton')}
            </Button>
          </div>

          {/* 注册链接 */}
          <div className="text-center mt-6">
            <span className="text-gray-400 text-sm">
              {t('login.noAccount')}{' '}
              <a
                onClick={() => navigate('/register')}
                className="text-[#f0b90b] hover:text-[#f0b90b]/80 cursor-pointer font-medium"
              >
                {t('login.signUpLink')}
              </a>
            </span>
          </div>
        </div>
      </div>

      {/* 聊天窗口或按钮 */}
      {showChat ? (
        <GuestRealTimeChatWindow 
          guestId={guestId}
          onClose={() => {
            setShowChat(false)
            setChatMinimized(false)
          }}
          onMinimize={() => {
            setChatMinimized(!chatMinimized)
          }}
          isMinimized={chatMinimized}
          onUnreadCountChange={(count) => setUnreadCount(count)}
        />
      ) : (
        <div className="fixed bottom-6 right-6 z-40">
          <Badge count={unreadCount} offset={[-5, 5]}>
            <button
              onClick={() => {
                setShowChat(true)
                setChatMinimized(false)
                setUnreadCount(0)
              }}
              className="w-14 h-14 bg-[#f0b90b] rounded-full flex items-center justify-center shadow-lg hover:bg-[#f0b90b]/90 transition-all"
            >
              <MessageOutlined className="text-black text-xl" />
            </button>
          </Badge>
        </div>
      )}
    </div>
  )
}

export default LoginPage